import React, { useState, useEffect, useMemo, useRef } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useSidebar } from "../../context/SidebarContext";
import ThemeMaker from "../../components/thememaker";
import { httpService } from "../../services/httpService";
import { toast } from "react-toastify";
import ProductOverview from "./ProductOverview";
import ProductDrawer from "../../components/inventory/ProductDrawer";
import ProductPurchaseHistory from "./ProductPurchaseHistory";
import ProductExpiryReturn from "./ProductExpiryReturn";
import ProductTransactionHistory from "./ProductTransactionHistory";
import * as XLSX from "xlsx";
import { createPortal } from "react-dom";
import ProductBatches from "./ProductBatches";

const TABS = [
  { key: "overview", label: "Overview" },
  { key: "expiryReturn", label: "Adjustments" },
  { key: "batches", label: "Batches" },
  { key: "transactionHistory", label: "Transaction History" },
];

// ── Export helper — mirrors Products.jsx buildExportRows ──
const buildExportRows = (products) =>
  products.map((p) => {
    const variant = p.variant || {};
    const spec = variant.specification || p.specification || "";
    const qtyUnit = variant.quantity_unit || p.quantity_unit || "";
    const variantLabel = p.variant_name
      ? `${p.variant_name}${spec ? ` (${spec}${qtyUnit ? ` ${qtyUnit}` : ""})` : ""}`
      : "";

    return {
      "Product Name":       p.name || "",
      "Variant":            variantLabel,
      "SKU":                p.sku_number || "",
      "Barcode":            p.barcode || "",
      "Category":           (p.product_category || []).map((c) => c.name).join(", "),
      "Subcategory":        (p.product_subcategory || []).map((s) => s.name).join(", "),
      "Brand":              typeof p.product_brand === "object" ? p.product_brand?.name : (p.product_brand || ""),
      "Unit":               variant.sales_unit || p.unit || "",
      "Packaging":          p.packaging || variant.packaging || "",
      "HSN Code":           p.hsn_code || "",
      "GST Slab (%)":       p.gst_slab || "",
      "Purchase Price (₹)": Number(p.purchase_price || 0),
      "Sale Price (₹)":     Number(p.effective_sale_price || p.sale_price || 0),
      "MRP (₹)":            Number(p.mrp || 0),
      "Stock Qty":          Number(p.quantity_available || 0),
      "Stock Value (₹)":    Number(p.purchase_price || 0) * Number(p.quantity_available || 0),
    };
  });

// ─── Reusable Badge ───────────────────────────────────────────────────────────
const Badge = ({ children, color = "#6366f1", bg = "#eef2ff" }) => (
  <span style={{
    display: "inline-block", padding: "2px 10px", borderRadius: "999px",
    fontSize: "11px", fontWeight: 600, color, background: bg,
  }}>
    {children}
  </span>
);

// ─── Hero Card (always visible) ───────────────────────────────────────────────
const ProductHeroCard = ({ product, siblingVariants = [], onEditClick }) => {
  const [activeImg, setActiveImg] = useState(0);
  if (!product) return null;

  const images = product.images || [];

  const getDiscountDisplay = (discount) => {
    if (!discount) return null;
    if (discount.type === "FLAT") return `FLAT ₹${discount.value} OFF`;
    if (discount.type === "PERCENT" || discount.type === "PERCENTAGE") {
      return `${discount.savings_percent || discount.value}% OFF`;
    }
    return discount.name;
  };

  const getDisplayName = () => {
    const variant = product?.variant;
    if (variant && variant.variant_name) return variant.variant_name;
    return product.name || "—";
  };

  const displayName = getDisplayName();

  return (
    <div style={{
      background: "linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)",
      borderRadius: "12px",
      border: "1px solid #bfdbfe",
      padding: "12px 16px",
      marginBottom: "12px",
      boxShadow: "0 1px 4px rgba(59,130,246,0.08)",
      display: "flex",
      gap: "12px",
      alignItems: "center",
      width: "100%",
      boxSizing: "border-box",
    }}>
      {images.length > 0 && (
        <div style={{ flexShrink: 0 }}>
          <div style={{
            width: "56px", height: "56px", borderRadius: "8px", overflow: "hidden",
            border: "1px solid #eef0f5", background: "#f9fafb",
          }}>
            <img
              src={images[activeImg]?.startsWith("http")
                ? images[activeImg]
                : `https://flps2025.s3.ap-south-1.amazonaws.com/${images[activeImg]}`}
              alt={product.name}
              style={{ width: "100%", height: "100%", objectFit: "cover" }}
              onError={(e) => {
                e.currentTarget.onerror = null;
                e.currentTarget.src = "/icons/default-product.svg";
              }}
            />
          </div>
        </div>
      )}

      <div style={{ flex: 1, minWidth: 0 }}>
        <h2 style={{
          margin: 0,
          fontSize: "16px",
          fontWeight: 700,
          color: "#111827",
          letterSpacing: "-0.02em",
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
        }}>
          {displayName}
        </h2>

        {product.product_brand && (
          <p style={{ margin: "2px 0 0", fontSize: "11px", color: "#6b7280" }}>
            by <strong>
              {typeof product.product_brand === "object"
                ? product.product_brand?.name
                : product.product_brand}
            </strong>
          </p>
        )}

        {product.description && (
          <p style={{
            margin: "2px 0 0", fontSize: "12px", color: "#6b7280", lineHeight: 1.3,
            whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
          }}>
            {product.description}
          </p>
        )}

        {siblingVariants.length > 0 && (
          <div style={{ marginTop: "4px", display: "flex", alignItems: "center", flexWrap: "wrap", gap: "4px" }}>
            <span style={{ fontSize: "10px", color: "#9ca3af", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em", flexShrink: 0 }}>
              Variants:
            </span>
            {siblingVariants.map((v) => {
              let siblingLabel = v.variant_name || v.sku_number || "—";
              const spec = v.variant?.specification;
              const unit = v.variant?.quantity_unit;
              if (spec) {
                siblingLabel += ` (${spec}${unit ? ` ${unit}` : ""})`;
              }

              return (
                <a
                  key={v._id}
                  href={`/admin/inventory-management/products/${v._id}`}
                  style={{
                    fontSize: "11px",
                    fontWeight: 600,
                    color: "#6366f1",
                    background: "#eef2ff",
                    padding: "1px 8px",
                    borderRadius: "999px",
                    textDecoration: "none",
                    border: "1px solid #c7d2fe",
                    whiteSpace: "nowrap",
                    transition: "all 0.15s",
                  }}
                >
                  {siblingLabel}
                </a>
              );
            })}
          </div>
        )}
      </div>

      <div style={{ width: "1px", alignSelf: "stretch", background: "#bfdbfe", flexShrink: 0 }} />

      <div style={{ flexShrink: 0, textAlign: "right" }}>
        <p style={{
          margin: 0, fontSize: "10px", fontWeight: 600,
          letterSpacing: "0.06em", textTransform: "uppercase", color: "#9ca3af",
        }}>Sale Price</p>

        <p style={{ margin: 0, fontSize: "20px", fontWeight: 800, color: "#059669", letterSpacing: "-0.02em", lineHeight: 1.2 }}>
          ₹{product.effective_sale_price || 0}
          {product.unit && (
            <span style={{ fontSize: "12px", fontWeight: 600, color: "#6b7280" }}> /{product.unit}</span>
          )}
        </p>

        <div style={{ display: "flex", alignItems: "center", gap: "6px", justifyContent: "flex-end", marginTop: "2px" }}>
          {product.mrp && product.mrp !== product.effective_sale_price && (
            <span style={{ fontSize: "11px", color: "#9ca3af", textDecoration: "line-through" }}>
              ₹{product.mrp}
            </span>
          )}
          {product?.applied_discount?.name && getDiscountDisplay(product.applied_discount) && (
            <Badge color="#ea580c" bg="#fff7ed">
              {getDiscountDisplay(product.applied_discount)}
            </Badge>
          )}
        </div>
      </div>

      <div style={{
        flexShrink: 0,
        background: product.quantity_available > 0 ? "#f0fdf4" : "#fef2f2",
        border: `1px solid ${product.quantity_available > 0 ? "#bbf7d0" : "#fecaca"}`,
        borderRadius: "8px", padding: "6px 14px", textAlign: "center", minWidth: "64px",
      }}>
        <p style={{
          margin: 0, fontSize: "9px", fontWeight: 700, letterSpacing: "0.06em",
          textTransform: "uppercase",
          color: product.quantity_available > 0 ? "#16a34a" : "#dc2626",
        }}>Stock</p>
        <p style={{
          margin: 0, fontSize: "18px", fontWeight: 800, lineHeight: 1.2,
          color: product.quantity_available > 0 ? "#16a34a" : "#dc2626",
        }}>
          {product.quantity_available || 0}
        </p>
      </div>

      <button
        onClick={onEditClick}
        title="Edit product"
        style={{
          flexShrink: 0,
          width: "32px",
          height: "32px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: "linear-gradient(135deg, #4f46e5, #6366f1)",
          color: "#fff",
          border: "none",
          borderRadius: "8px",
          fontSize: "14px",
          cursor: "pointer",
          boxShadow: "0 2px 6px rgba(99,102,241,0.35)",
          transition: "transform 0.15s",
        }}
        onMouseEnter={(e) => (e.currentTarget.style.transform = "scale(1.08)")}
        onMouseLeave={(e) => (e.currentTarget.style.transform = "scale(1)")}
      >
        ✏️
      </button>
    </div>
  );
};

// ─── Sidebar skeleton row ──────────────────────────────────────────────────
const SidebarSkeletonRow = () => (
  <div className="px-3 py-2.5 border-b border-gray-50">
    <div className="h-3.5 w-3/4 bg-gray-200 rounded animate-pulse mb-2" />
    <div className="h-2.5 w-1/2 bg-gray-100 rounded animate-pulse" />
  </div>
);

// ─── Hero card skeleton ────────────────────────────────────────────────────
const HeroCardSkeleton = () => (
  <div style={{
    background: "#f3f4f6", borderRadius: "12px", border: "1px solid #e5e7eb",
    padding: "16px 20px", marginBottom: "12px", display: "flex", gap: "16px",
    alignItems: "center", width: "100%", boxSizing: "border-box",
  }}>
    <div className="w-[90px] h-[90px] bg-gray-200 rounded-lg animate-pulse flex-shrink-0" />
    <div className="flex-1 min-w-0 space-y-2">
      <div className="h-3 w-24 bg-gray-200 rounded animate-pulse" />
      <div className="h-5 w-1/2 bg-gray-200 rounded animate-pulse" />
      <div className="h-3 w-3/4 bg-gray-100 rounded animate-pulse" />
    </div>
    <div className="w-20 h-16 bg-gray-200 rounded animate-pulse flex-shrink-0" />
  </div>
);

const BulkStockUpdateModal = ({ products, onClose, onSave, saving }) => {
  const [openingStock, setOpeningStock] = useState("");
  const [confirmed, setConfirmed] = useState(false);

  const handleSubmit = () => {
    if (openingStock === "" || isNaN(Number(openingStock))) {
      toast.error("Please enter a valid stock value");
      return;
    }
    if (!confirmed) {
      toast.error("Please confirm before proceeding");
      return;
    }

    const updates = products.map((p) => ({
      _id: p._id,
      inventory_id: p.inventory_id,
      quantity_available: Number(openingStock),
    }));
    onSave(updates);
  };

  return createPortal(
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-[9999] p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-md"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
          <h3 className="text-lg font-bold text-gray-900">Bulk Stock Update</h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 text-xl leading-none"
          >
            ×
          </button>
        </div>

        <div className="px-5 py-5 space-y-4">
          <p className="text-sm text-gray-600">
            You're updating stock for <strong>{products.length}</strong> selected product{products.length > 1 ? "s" : ""}.
          </p>

          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">
              Opening Stock
            </label>
            <input
              type="number"
              value={openingStock}
              onChange={(e) => setOpeningStock(e.target.value)}
              placeholder="Enter stock quantity"
              className="w-full px-3 py-2.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              autoFocus
            />
          </div>

          <div className="bg-amber-50 border border-amber-200 rounded-lg px-3 py-2.5 flex items-start gap-2">
            <svg className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
            </svg>
            <p className="text-xs text-amber-800 leading-relaxed">
              This value will be applied as the new stock for <strong>all {products.length} selected product{products.length > 1 ? "s" : ""}</strong>,
              overwriting their current stock. This action cannot be undone.
            </p>
          </div>

          <label className="flex items-center gap-2 text-xs text-gray-600 cursor-pointer">
            <input
              type="checkbox"
              checked={confirmed}
              onChange={(e) => setConfirmed(e.target.checked)}
              className="w-4 h-4 accent-blue-600 cursor-pointer"
            />
            I understand this will overwrite stock and cannot be undone
          </label>
        </div>

        <div className="px-5 py-4 border-t border-gray-100 flex justify-end gap-2">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-600 hover:bg-gray-50 rounded-lg transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={saving || !confirmed || openingStock === ""}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saving ? "Updating..." : "Update Stock"}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

// ─── Product List Sidebar ───────────────────────────────────────────────────
const ProductListSidebar = ({
  products,
  loadingList,
  activeProductId,
  activeInventoryId,
  onSelect,
  search,
  setSearch,
  onAddClick,
  onImportClick,
  onExportClick,
  onImportHistoryClick,
  onBulkStockSave,
}) => {
  const [showSortMenu, setShowSortMenu] = useState(false);
  const [showMoreMenu, setShowMoreMenu] = useState(false);
  const [sortBy, setSortBy] = useState("default");

  // ── Bulk select state ──
  const [bulkMode, setBulkMode] = useState(false);
  const [selectedKeys, setSelectedKeys] = useState(new Set());
  const [showBulkActionsMenu, setShowBulkActionsMenu] = useState(false);
  const [showStockModal, setShowStockModal] = useState(false);
  const [savingBulk, setSavingBulk] = useState(false);

  const sortMenuRef = useRef(null);
  const moreMenuRef = useRef(null);
  const bulkActionsRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (sortMenuRef.current && !sortMenuRef.current.contains(e.target)) {
        setShowSortMenu(false);
      }
      if (moreMenuRef.current && !moreMenuRef.current.contains(e.target)) {
        setShowMoreMenu(false);
      }
      if (bulkActionsRef.current && !bulkActionsRef.current.contains(e.target)) {
        setShowBulkActionsMenu(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const filtered = !search.trim()
    ? products
    : products.filter((p) => {
        const q = search.toLowerCase();
        return (
          p.name?.toLowerCase().includes(q) ||
          p.sku_number?.toLowerCase().includes(q) ||
          p.variant_name?.toLowerCase().includes(q) ||
          p.barcode?.toLowerCase().includes(q)
        );
      });

  const sorted = useMemo(() => {
    const arr = [...filtered];
    switch (sortBy) {
      case "name_asc":
        return arr.sort((a, b) =>
          (a.variant_name || a.name || "").localeCompare(b.variant_name || b.name || "")
        );
      case "name_desc":
        return arr.sort((a, b) =>
          (b.variant_name || b.name || "").localeCompare(a.variant_name || a.name || "")
        );
      case "stock_asc":
        return arr.sort((a, b) => Number(a.quantity_available || 0) - Number(b.quantity_available || 0));
      case "stock_desc":
        return arr.sort((a, b) => Number(b.quantity_available || 0) - Number(a.quantity_available || 0));
      default:
        return arr;
    }
  }, [filtered, sortBy]);

  const sortOptions = [
    { key: "default", label: "Default" },
    { key: "name_asc", label: "Name (A → Z)" },
    { key: "name_desc", label: "Name (Z → A)" },
    { key: "stock_desc", label: "Stock (High → Low)" },
    { key: "stock_asc", label: "Stock (Low → High)" },
  ];

  const rowKey = (p) => `${p._id}-${p.inventory_id}`;

  const toggleBulkMode = () => {
    setBulkMode((prev) => {
      const next = !prev;
      if (!next) setSelectedKeys(new Set());
      return next;
    });
  };

  const toggleSelect = (p) => {
    const key = rowKey(p);
    setSelectedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const displayTotalProducts = sorted.length;
  const displayTotalQuantity = useMemo(
    () => sorted.reduce((sum, p) => sum + Math.max(0, Number(p?.quantity_available || 0)), 0),
    [sorted]
  );

  const selectedProducts = useMemo(
    () => sorted.filter((p) => selectedKeys.has(rowKey(p))),
    [sorted, selectedKeys]
  );

  const handleBulkStockSubmit = async (updates) => {
    setSavingBulk(true);
    try {
      await onBulkStockSave?.(updates);
      setShowStockModal(false);
      setBulkMode(false);
      setSelectedKeys(new Set());
    } finally {
      setSavingBulk(false);
    }
  };

  return (
    <div
      className="hidden md:flex flex-col bg-white border-r border-gray-200 sticky top-0 h-screen flex-shrink-0"
      style={{ width: "360px" }}
    >
      {/* Sort + Search + More */}
      <div className="p-2.5 border-b border-gray-100 flex items-center gap-1.5">
        {/* Sort button */}
        <div className="relative" ref={sortMenuRef}>
          <button
            onClick={() => {
              setShowSortMenu((prev) => !prev);
              setShowMoreMenu(false);
            }}
            title="Sort"
            className={`flex-shrink-0 w-9 h-9 flex items-center justify-center rounded-lg border transition-colors ${
              sortBy !== "default"
                ? "bg-blue-50 border-blue-200 text-blue-600"
                : "bg-white border-gray-200 text-gray-600 hover:bg-gray-50"
            }`}
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M3 7h13M3 12h9m-9 5h5M17 4v16m0 0l-3-3m3 3l3-3" />
            </svg>
          </button>

          {showSortMenu && (
            <div className="absolute left-0 mt-2 w-48 bg-white rounded-xl shadow-lg border border-gray-100 overflow-hidden z-50">
              {sortOptions.map((opt) => (
                <button
                  key={opt.key}
                  onClick={() => {
                    setSortBy(opt.key);
                    setShowSortMenu(false);
                  }}
                  className={`w-full text-left px-4 py-2 text-sm transition-colors ${
                    sortBy === opt.key
                      ? "bg-blue-50 text-blue-700 font-semibold"
                      : "text-gray-700 hover:bg-gray-50"
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Search input */}
        <div className="relative flex-1 min-w-0">
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search in Items"
            className="w-full pl-8 pr-2 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-gray-50"
          />
          <svg
            className="absolute left-2.5 top-2.5 w-3.5 h-3.5 text-gray-400 pointer-events-none"
            fill="none" stroke="currentColor" viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
              d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
        </div>

        {/* Add Product button outside */}
        <div className="relative group">
          <button
            onClick={onAddClick}
            className="flex-shrink-0 w-9 h-9 flex items-center justify-center rounded-lg bg-blue-600 text-white hover:bg-blue-700 active:bg-blue-800 shadow-sm transition-colors"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
            </svg>
          </button>
          <div className="absolute left-1/2 -translate-x-1/2 top-full mt-1.5 whitespace-nowrap rounded-md bg-gray-900 text-white px-2 py-1 text-xs opacity-0 group-hover:opacity-100 transition-opacity duration-150 pointer-events-none z-50 shadow-md">
            Add Product
          </div>
        </div>

        {/* More actions (3-dot) button */}
        <div className="relative" ref={moreMenuRef}>
          <button
            onClick={() => {
              setShowMoreMenu((prev) => !prev);
              setShowSortMenu(false);
            }}
            title="More actions"
            className={`flex-shrink-0 w-9 h-9 flex items-center justify-center rounded-lg border transition-colors ${
              showMoreMenu
                ? "bg-blue-50 border-blue-200 text-blue-600"
                : "bg-white border-gray-200 text-gray-600 hover:bg-gray-50 hover:text-gray-900"
            }`}
          >
            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
              <circle cx="12" cy="5" r="1.8" />
              <circle cx="12" cy="12" r="1.8" />
              <circle cx="12" cy="19" r="1.8" />
            </svg>
          </button>

          {showMoreMenu && (
            <div className="absolute right-0 mt-2 w-52 bg-white rounded-xl shadow-lg border border-gray-100 overflow-hidden z-50">
              {/* <button
                onClick={() => {
                  setShowMoreMenu(false);
                  onAddClick?.();
                }}
                className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
              >
                <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
                </svg>
                Add Product
              </button> */}

              <button
                onClick={() => {
                  setShowMoreMenu(false);
                  onImportClick?.();
                }}
                className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
              >
                <svg className="w-4 h-4 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v2a2 2 0 002 2h12a2 2 0 002-2v-2M7 10l5 5 5-5M12 15V3" />
                </svg>
                Import
              </button>

              <button
                onClick={() => {
                  setShowMoreMenu(false);
                  onExportClick?.();
                }}
                className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
              >
                <svg className="w-4 h-4 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v2a2 2 0 002 2h12a2 2 0 002-2v-2M17 8l-5-5-5 5M12 3v12" />
                </svg>
                Export
              </button>

              <button
                onClick={() => {
                  setShowMoreMenu(false);
                  onImportHistoryClick?.();
                }}
                className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
              >
                <svg className="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                Import History
              </button>

              <div className="border-t border-gray-100" />

              {/* <button
                onClick={() => {
                  setShowMoreMenu(false);
                  toggleBulkMode();
                }}
                className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
              >
                <svg className="w-4 h-4 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h.582M20 20v-5h-.581M5.635 8.5a8 8 0 0113.148-3.148M18.365 15.5a8 8 0 01-13.148 3.148" />
                </svg>
                {bulkMode ? "Exit Bulk Update" : "Bulk Update"}
              </button> */}
            </div>
          )}
        </div>
      </div>

      {/* Summary Cards */}
      <div className="px-3 py-2 bg-gray-50/70 border-b border-gray-100 grid grid-cols-2 gap-2">
        <div className="bg-white px-2.5 py-1.5 rounded-xl border border-gray-200/80 shadow-xs text-center">
          <p className="text-[10px] uppercase tracking-widest font-semibold text-gray-500">
            TOTAL PRODUCTS
          </p>
          {loadingList ? (
            <div className="h-5 w-12 bg-gray-200 rounded animate-pulse mx-auto mt-0.5" />
          ) : (
            <p className="text-base font-extrabold text-blue-700 leading-tight mt-0.5">
              {displayTotalProducts.toLocaleString("en-IN")}
            </p>
          )}
        </div>
        <div className="bg-white px-2.5 py-1.5 rounded-xl border border-gray-200/80 shadow-xs text-center">
          <p className="text-[10px] uppercase tracking-widest font-semibold text-gray-500">
            TOTAL STOCK QTY
          </p>
          {loadingList ? (
            <div className="h-5 w-12 bg-gray-200 rounded animate-pulse mx-auto mt-0.5" />
          ) : (
            <p className="text-base font-extrabold text-indigo-700 leading-tight mt-0.5">
              {displayTotalQuantity.toLocaleString("en-IN")}
            </p>
          )}
        </div>
      </div>

      {/* Bulk selection bar — appears when bulkMode is on */}
      {bulkMode && (
        <div className="px-3 py-2 border-b border-gray-100 bg-blue-50 flex items-center justify-between gap-2">
          <span className="text-xs font-semibold text-blue-700">
            {selectedKeys.size > 0
              ? `${selectedKeys.size} selected`
              : "Select products to bulk update"}
          </span>

          <div className="flex items-center gap-2">
            {selectedKeys.size > 0 && (
              <div className="relative" ref={bulkActionsRef}>
                <button
                  onClick={() => setShowBulkActionsMenu((prev) => !prev)}
                  className="px-3 py-1.5 text-xs font-semibold text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors"
                >
                  Bulk Actions
                </button>

                {showBulkActionsMenu && (
                  <div className="absolute right-0 mt-2 w-44 bg-white rounded-xl shadow-lg border border-gray-100 overflow-hidden z-50">
                    <button
                      onClick={() => {
                        setShowBulkActionsMenu(false);
                        setShowStockModal(true);
                      }}
                      className="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                    >
                      <svg className="w-4 h-4 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                      </svg>
                      Stock Update
                    </button>
                  </div>
                )}
              </div>
            )}

            <button
              onClick={toggleBulkMode}
              className="text-xs font-medium text-gray-500 hover:text-gray-700"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* List */}
      <div className="flex-1 overflow-y-auto">
        {loadingList ? (
          <div>
            {Array.from({ length: 8 }).map((_, i) => <SidebarSkeletonRow key={i} />)}
          </div>
        ) : sorted.length === 0 ? (
          <div className="text-center text-gray-400 text-sm py-10 px-3">No products found</div>
        ) : (
          sorted.map((p) => {
            const key = rowKey(p);
            const isActive = String(p._id) === String(activeProductId) && String(p.inventory_id) === String(activeInventoryId);
            const isChecked = selectedKeys.has(key);

            const label =
              p.variant_name && !p.variant_name.toLowerCase().startsWith((p.name || "").toLowerCase())
                ? `${p.name} - ${p.variant_name}`
                : p.variant_name || p.name || "Unnamed Product";

            return (
              <div
                key={key}
                onClick={() => (bulkMode ? toggleSelect(p) : onSelect(p._id, p.inventory_id))}
                title={label}
                className={`w-full text-left px-3 py-2.5 border-b border-gray-50 transition-colors cursor-pointer flex items-start gap-2 ${
                  isActive && !bulkMode
                    ? "bg-blue-50 border-l-4 border-l-blue-600"
                    : isChecked
                    ? "bg-blue-50 border-l-4 border-l-blue-400"
                    : "hover:bg-gray-50 border-l-4 border-l-transparent"
                }`}
              >
                {bulkMode && (
                  <input
                    type="checkbox"
                    checked={isChecked}
                    onChange={() => toggleSelect(p)}
                    onClick={(e) => e.stopPropagation()}
                    className="mt-1 w-4 h-4 flex-shrink-0 accent-blue-600 cursor-pointer"
                  />
                )}

                <div className="min-w-0 flex-1">
                  <div className="flex items-start justify-between gap-2">
                    <p
                      className={`text-sm leading-snug ${isActive ? "text-blue-700 font-semibold" : "text-gray-800 font-medium"}`}
                      style={{
                        display: "-webkit-box",
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: "vertical",
                        overflow: "hidden",
                      }}
                    >
                      {label}
                    </p>
                    <span className="text-sm font-semibold flex-shrink-0 whitespace-nowrap">
                      ₹{Number(p.effective_sale_price ?? p.sale_price ?? p.mrp ?? 0).toLocaleString("en-IN")}
                    </span>
                  </div>
                  <div className="flex items-center justify-between mt-0.5">
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <p className="text-xs text-gray-400">SKU: {p.sku_number || "—"}</p>
                      {(p.packaging || p.variant?.packaging) && (
                        <span className="text-[10px] bg-gray-100 text-gray-600 px-1.5 py-0.2 rounded font-medium">
                          {p.packaging || p.variant?.packaging}
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-gray-400">Stock: {p.quantity_available ?? 0}</p>
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Bulk Stock Update Modal */}
      {showStockModal && (
        <BulkStockUpdateModal
          products={selectedProducts}
          saving={savingBulk}
          onClose={() => setShowStockModal(false)}
          onSave={handleBulkStockSubmit}
        />
      )}
    </div>
  );
};

// ─── Main View ────────────────────────────────────────────────────────────────
const ProductView = () => {
  const navigate = useNavigate();
  const { productId } = useParams();
  const [searchParams] = useSearchParams();
  const inventoryId = searchParams.get("inventoryId");
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(false);

  const [activeTab, setActiveTab] = useState(() => {
    return localStorage.getItem("productActiveTab") || "overview";
  });

  const [productList, setProductList] = useState([]);
  const [loadingList, setLoadingList] = useState(true);
  const [listSearch, setListSearch] = useState("");

  const [isEditDrawerOpen, setIsEditDrawerOpen] = useState(false);
  const [editProductData, setEditProductData] = useState(null);
  const [editLoading, setEditLoading] = useState(false);

  const [isAddDrawerOpen, setIsAddDrawerOpen] = useState(false);

  useEffect(() => {
    setActiveTab("overview");
    localStorage.removeItem("productActiveTab");
  }, [productId]);

  const handleTabChange = (tabKey) => {
    setActiveTab(tabKey);
    localStorage.setItem("productActiveTab", tabKey);
  };

  useEffect(() => {
    fetchProductDetails();
  }, [productId, inventoryId]);

  useEffect(() => {
    fetchProductList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!productId && !loadingList && productList.length > 0) {
      const first = productList[0];
      navigate(
        `/admin/inventory-management/products/${first._id}${
          first.inventory_id ? `?inventoryId=${first.inventory_id}` : ""
        }`,
        { replace: true }
      );
    }
  }, [productId, loadingList, productList, navigate]);

  const fetchProductList = async () => {
    setLoadingList(true);
    try {
      const response = await httpService.get(`/vendorproduct/getvendorproducts`);
      if (response.code === 200) {
        const list = Array.isArray(response?.data) ? response.data : [];
        const validList = list.filter((p) => p && typeof p === "object");
        setProductList(validList);

        if (!productId && validList.length > 0) {
          const first = validList[0];
          navigate(
            `/admin/inventory-management/products/${first._id}${
              first.inventory_id ? `?inventoryId=${first.inventory_id}` : ""
            }`,
            { replace: true }
          );
        }
      }
    } catch (err) {
      console.error("Error fetching product list:", err);
    } finally {
      setLoadingList(false);
    }
  };

  const handleSelectProduct = (id, inventoryId) => {
    if (id === productId && inventoryId === (product?.inventory_id)) return;
    navigate(`/admin/inventory-management/products/${id}?inventoryId=${inventoryId}`);
  };

  const fetchProductDetails = async () => {
    if (!productId) {
      setProduct(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const url = inventoryId
        ? `/vendorproduct/getvendorproduct/${productId}?inventoryId=${inventoryId}`
        : `/vendorproduct/getvendorproduct/${productId}`;

      const response = await httpService.get(url);
      if (response.code === 200) setProduct(response.data);
    } catch (err) {
      console.error(err);
      toast.error("Failed to fetch product details");
    } finally {
      setLoading(false);
    }
  };

  const handleProductUpdated = (updatedProduct) => {
    setProduct(updatedProduct);
    setProductList((prev) =>
      prev.map((p) => (p._id === updatedProduct._id ? { ...p, ...updatedProduct } : p))
    );
  };

  const prepareEditProductData = (data) => {
    if (!data) return null;
    const productAttributes = data.attributes || [];
    if (data.variant) {
      return {
        ...data,
        variant_name: data.variant.variant_name || null,
        specification: data.variant.specification || null,
        quantity_unit: data.variant.quantity_unit || null,
        sales_unit: data.variant.sales_unit || null,
        variant: data.variant,
        attributes: productAttributes,
      };
    }
    return { ...data, attributes: productAttributes };
  };

  const refreshProductData = async (showToast = false) => {
    if (!productId) return;
    try {
      const response = await httpService.get(`/vendorproduct/getvendorproduct/${productId}`);
      if (response.code === 200) {
        setProduct(response.data);
        if (showToast) toast.success("Product data refreshed");
      }
    } catch (err) {
      console.error("Error refreshing product data:", err);
    }
  };

  const handleEditClick = async () => {
    if (!product?._id) return;
    setIsEditDrawerOpen(true);
    setEditLoading(true);
    try {
      const url = product?.inventory_id
        ? `/vendorproduct/getvendorproduct/${product._id}?inventoryId=${product.inventory_id}`
        : `/vendorproduct/getvendorproduct/${product._id}`;

      const response = await httpService.get(url);
      if (response.status === true && response.code === 200) {
        setEditProductData(response.data);
      } else {
        toast.error("Failed to load product details");
        setEditProductData(product);
      }
    } catch (error) {
      console.error("Error fetching product details:", error);
      toast.error("Failed to load product details");
      setEditProductData(product);
    } finally {
      setEditLoading(false);
    }
  };

  const handleDrawerSave = (updatedProduct) => {
    if (updatedProduct) {
      toast.success("Product updated successfully");
      handleProductUpdated(updatedProduct);
    }
    setIsEditDrawerOpen(false);
    refreshProductData();
  };

  const handleDrawerClose = () => {
    setIsEditDrawerOpen(false);
    setEditProductData(null);
    refreshProductData(false);
  };

  const handleAddDrawerSave = (newProduct) => {
    if (newProduct) {
      toast.success("Product added successfully");
      fetchProductList();
      if (newProduct._id) {
        navigate(
          `/admin/inventory-management/products/${newProduct._id}${
            newProduct.inventory_id ? `?inventoryId=${newProduct.inventory_id}` : ""
          }`
        );
      }
    }
    setIsAddDrawerOpen(false);
  };

  const handleAddDrawerClose = () => {
    setIsAddDrawerOpen(false);
  };

  const handleExportProducts = () => {
    if (!productList || productList.length === 0) {
      toast.warning("No products to export");
      return;
    }
    try {
      const rows = buildExportRows(productList);
      const worksheet = XLSX.utils.json_to_sheet(rows);
      worksheet["!cols"] = [
        { wch: 30 }, { wch: 20 }, { wch: 16 }, { wch: 16 }, { wch: 16 },
        { wch: 16 }, { wch: 16 }, { wch: 10 }, { wch: 12 }, { wch: 12 },
        { wch: 18 }, { wch: 15 }, { wch: 12 }, { wch: 10 }, { wch: 16 },
      ];
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Products");
      const fileName = `products_export_${new Date().toISOString().split("T")[0]}.xlsx`;
      XLSX.writeFile(workbook, fileName);
      toast.success(`${productList.length} product(s) exported successfully`);
    } catch (error) {
      console.error("Export error:", error);
      toast.error("Failed to export products");
    }
  };

  // ── Bulk stock update — plug in your real endpoint here ──
  const handleBulkStockSave = async (updates) => {
    try {
      const response = await httpService.post(`/vendorproduct/bulkUpdate`, {
        type: "stock",
        updates, // [{ _id, inventory_id, quantity_available }]
      });

      if (response.code === 200 && response.status) {
        const { success = [], failed = [] } = response.data || {};

        // Update local sidebar list for products that succeeded
        if (success.length > 0) {
          setProductList((prev) =>
            prev.map((p) => {
              const match = success.find(
                (s) => String(s.product_id) === String(p._id)
              );
              return match ? { ...p, quantity_available: match.quantity_available } : p;
            })
          );
        }

        // If the currently open product was part of the update, refresh its detail view
        if (success.some((s) => String(s.product_id) === String(productId))) {
          refreshProductData();
        }

        if (failed.length > 0) {
          toast.warning(`${success.length} updated, ${failed.length} failed`);
        } else {
          toast.success(`Stock updated for ${success.length} product(s)`);
        }
      } else {
        toast.error(response.message || "Failed to update stock");
      }
    } catch (err) {
      console.error("Bulk stock update error:", err);
      toast.error(err.response?.data?.message || "Failed to update stock");
    }
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case "overview":
        return <ProductOverview product={product} onProductUpdated={handleProductUpdated} onEditClick={handleEditClick} />;
      case "purchaseHistory":
        return <ProductPurchaseHistory productId={productId} inventoryId={inventoryId} />;
      case "expiryReturn":
        return <ProductExpiryReturn productId={productId}  inventoryId={inventoryId}/>;
      case "batches":                                                              
        return <ProductBatches batches={product?.batches || []} />;
      case "transactionHistory":
        return <ProductTransactionHistory productId={productId} inventoryId={inventoryId}  product={product}  />;
      default:
        return <div style={{ padding: 40 }}>Content</div>;
    }
  };

  if (loadingList && productList.length === 0) {
    return (
      <ThemeMaker>
        <div className="w-full min-h-[70vh] flex flex-col items-center justify-center">
          <div className="w-10 h-10 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mb-3"></div>
          <p className="text-sm text-gray-500 font-medium">Loading products...</p>
        </div>
      </ThemeMaker>
    );
  }

  if (!loadingList && productList.length === 0) {
    return (
      <ThemeMaker>
        <div className="w-full min-h-[80vh] flex flex-col items-center justify-center p-6">
          <div className="max-w-md w-full text-center bg-white p-8 rounded-2xl shadow-sm border border-gray-100 flex flex-col items-center">
            <div className="w-20 h-20 rounded-full bg-blue-50 text-blue-600 flex items-center justify-center mb-5 shadow-inner">
              <svg className="w-10 h-10 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
              </svg>
            </div>
            <h2 className="text-2xl font-bold text-gray-800 mb-2">No Products in Inventory</h2>
            <p className="text-gray-500 text-sm mb-6 max-w-sm">
              Your inventory is currently empty. Add your first product to start tracking inventory, batches, and sales.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 w-full justify-center">
              <button
                type="button"
                onClick={() => setIsAddDrawerOpen(true)}
                className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-blue-600 hover:bg-blue-700 active:bg-blue-800 text-white font-semibold rounded-xl shadow-md transition-all duration-200"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
                </svg>
                Add Product
              </button>
              <button
                type="button"
                onClick={() => navigate("/admin/inventory-management/product-import")}
                className="inline-flex items-center justify-center gap-2 px-5 py-3 border border-gray-300 hover:bg-gray-50 text-gray-700 font-semibold rounded-xl transition-all duration-200"
              >
                Import Products
              </button>
            </div>
          </div>
        </div>

        <ProductDrawer
          isOpen={isAddDrawerOpen}
          productData={null}
          inventoryId={null}
          newProductName=""
          onClose={handleAddDrawerClose}
          onSave={handleAddDrawerSave}
        />
      </ThemeMaker>
    );
  }

  return (
    <ThemeMaker>
      <div className="w-full min-h-screen flex">

        <ProductListSidebar
          products={productList}
          loadingList={loadingList}
          activeProductId={productId}
          activeInventoryId={inventoryId}
          onSelect={handleSelectProduct}
          search={listSearch}
          setSearch={setListSearch}
          onAddClick={() => setIsAddDrawerOpen(true)}
          onImportClick={() => navigate("/admin/inventory-management/product-import")}
          onExportClick={handleExportProducts}
          onImportHistoryClick={() => navigate("/admin/inventory-management/product-import-history")}
          onBulkStockSave={handleBulkStockSave}
        />

        <div className="flex-1 min-w-0 px-4 relative flex flex-col">
          <div className="flex flex-col gap-4 w-full max-w-full">
            <div className="flex-1 w-full max-w-full">

              <div
                className="flex space-x-2 md:space-x-4 rounded-t-md px-4 py-3 w-full bg-white shadow-sm sticky top-0 z-10"
                style={{ boxShadow: "0 2px 8px 0 rgba(0,0,0,0.08)" }}
              >
                {TABS.map((tab) => (
                  <button
                    key={tab.key}
                    className={`px-4 py-2 text-sm md:text-base font-medium rounded-lg transition-all duration-200
                      ${activeTab === tab.key
                        ? "bg-gradient-to-r from-blue-500 to-blue-600 text-white shadow-md"
                        : "text-gray-600 hover:text-blue-600 hover:bg-blue-50"
                      }`}
                    onClick={() => handleTabChange(tab.key)}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>

              <div className="mt-4">
                {loading ? (
                  <HeroCardSkeleton />
                ) : product ? (
                  <>
                    <ProductHeroCard product={product} onEditClick={handleEditClick} />

                    <div className="min-h-[400px] w-full">
                      {renderTabContent()}
                    </div>
                  </>
                ) : (
                  <div className="text-center text-gray-500 mt-12 py-8">
                    No product details found.
                  </div>
                )}
              </div>

              

            </div>
          </div>
        </div>

      </div>

      <ProductDrawer
        isOpen={isEditDrawerOpen}
        productData={prepareEditProductData(editProductData)}
        inventoryId={product?.inventory_id || editProductData?.inventory_id}
        onClose={handleDrawerClose}
        onSave={handleDrawerSave}
      />

      <ProductDrawer
        isOpen={isAddDrawerOpen}
        productData={null}
        inventoryId={null}
        newProductName={listSearch.trim()}
        onClose={handleAddDrawerClose}
        onSave={handleAddDrawerSave}
      />
    </ThemeMaker>
  );
};

export default ProductView;